import React from "react";
import useSEO from "../hooks/useSEO";
import { BIZ_REF } from "../seo";
import { Link } from "react-router-dom";
import { Phone, ArrowRight, CheckCircle2, ChevronRight, MapPin, Zap } from "lucide-react";
import { company, serviceAreas } from "../mock";
import { getLandingPage, LANDING_BASE } from "../landingPages";
import ContactForm from "../components/ContactForm";
import Testimonials from "../components/Testimonials";
import FAQ from "../components/FAQ";
import JsonLd from "../components/JsonLd";

const trustBadges = [
  "Licensed & Insured",
  "Free Roof Inspections",
  "24/7 Storm Response",
  "GAF Master Elite",
];

const LandingPage = ({ pageKey }) => {
  const page = getLandingPage(pageKey);

  useSEO({ title: page?.metaTitle, description: page?.metaDesc, image: page?.heroImage });

  if (!page) return null;

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: `${LANDING_BASE}/` },
      { "@type": "ListItem", position: 2, name: page.h1, item: `${LANDING_BASE}${page.path}` },
    ],
  };
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: page.serviceType,
    name: page.h1,
    provider: BIZ_REF,
    url: `${LANDING_BASE}${page.path}`,
    areaServed: ["Northern Virginia", "Washington D.C.", "Maryland"],
    description: page.metaDesc,
  };

  const nearby = serviceAreas.slice(0, 10);

  return (
    <>
      <JsonLd data={breadcrumbSchema} id="landing-breadcrumb" />
      <JsonLd data={serviceSchema} id="landing-service" />

      {/* Hero */}
      <section className="relative bg-brand-navy overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-slate to-brand-navy" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand-orange/10 rounded-full blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="flex flex-wrap items-center gap-2 text-sm text-white/60 mb-4">
              <Link to="/" className="hover:text-brand-orange transition-colors">Home</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-brand-orange">{page.h1}</span>
            </div>
            <p className="text-brand-orange font-semibold tracking-[0.18em] text-sm uppercase mb-3">
              {page.eyebrow}
            </p>
            <h1 className="font-display font-extrabold text-white text-3xl md:text-4xl lg:text-5xl leading-tight" data-testid="landing-h1">
              {page.h1}
            </h1>
            {page.urgency && (
              <div className="inline-flex items-center gap-2 mt-5 bg-brand-orange text-white font-bold px-4 py-2.5 rounded-full text-sm md:text-base shadow-lg" data-testid="landing-urgency">
                <Zap className="w-5 h-5 fill-white" />
                {page.urgency}
              </div>
            )}
            <div className="flex flex-wrap gap-3 mt-7">
              <a href={company.phoneHref} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5" data-testid="landing-call-btn">
                <Phone className="w-5 h-5" /> Call {company.phone}
              </a>
              <Link to="/contact" className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all" data-testid="landing-cta-btn">
                {page.ctaHeading} <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-8">
              {trustBadges.map((b) => (
                <div key={b} className="flex items-center gap-2 text-white/85 text-sm">
                  <CheckCircle2 className="w-5 h-5 text-brand-orange" /> {b}
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden shadow-2xl border-4 border-white/10">
            <img src={page.heroImage} alt={page.heroAlt} className="w-full h-[320px] object-cover" />
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="bg-brand-cream py-16">
        <div className="max-w-3xl mx-auto px-6 space-y-4 text-slate-700 leading-relaxed text-[17px]">
          {page.intro.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      </section>

      {/* Content sections */}
      <section className="bg-white py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-10">
          {page.sections.map((s, i) => (
            <div key={i}>
              <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl mb-3">
                {s.h2}
              </h2>
              {s.body && s.body.map((p, j) => (
                <p key={j} className="text-slate-600 leading-relaxed mb-3">{p}</p>
              ))}
              {s.bullets && (
                <ul className="grid sm:grid-cols-2 gap-2.5 mt-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-slate-700">
                      <CheckCircle2 className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" />
                      {b}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Service areas */}
      <section className="bg-brand-sand py-14">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-brand-navy text-2xl md:text-3xl mb-6">
            Serving Northern Virginia, D.C. &amp; Maryland
          </h2>
          <div className="flex flex-wrap justify-center gap-3">
            {nearby.map((a) => (
              <Link
                key={a.slug}
                to={`/service-areas/${a.slug}`}
                className="inline-flex items-center gap-1.5 bg-white border border-black/10 text-brand-navy rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors"
              >
                <MapPin className="w-3.5 h-3.5" /> {a.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Reviews */}
      <Testimonials />

      {/* Lead form */}
      <ContactForm />

      {/* FAQ (emits FAQ schema) */}
      <FAQ heading={`${page.h1} — FAQs`} items={page.faqs} />

      {/* Related links */}
      {page.related && (
        <section className="bg-brand-sand py-14">
          <div className="max-w-5xl mx-auto px-6 text-center">
            <h2 className="font-display font-extrabold text-brand-navy text-2xl mb-6">
              Related Services
            </h2>
            <div className="flex flex-wrap justify-center gap-3">
              {page.related.map((r) => (
                <Link
                  key={r.to}
                  to={r.to}
                  className="inline-flex items-center gap-1.5 bg-white border border-black/10 text-brand-navy rounded-full px-4 py-2 text-sm font-medium hover:bg-brand-orange hover:text-white hover:border-brand-orange transition-colors"
                >
                  {r.label}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="bg-brand-navy py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display font-extrabold text-white text-2xl md:text-3xl mb-4">
            {page.ctaHeading}
          </h2>
          <p className="text-white/70 mb-8 max-w-xl mx-auto">
            Free inspections and honest, upfront pricing across Northern Virginia and the DMV.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <a href={company.phoneHref} className="inline-flex items-center gap-2 bg-brand-orange hover:bg-brand-orangedark text-white font-semibold px-6 py-3.5 rounded-full shadow-lg transition-all hover:-translate-y-0.5">
              <Phone className="w-5 h-5" /> Call {company.phone}
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 bg-white/5 border border-white/25 text-white font-semibold px-6 py-3.5 rounded-full hover:bg-white/10 transition-all">
              Request a Free Estimate <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default LandingPage;
